import React from 'react';
import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import {
  Calendar,
  UtensilsCrossed,
  Users,
  Monitor,
  MessageSquare,
  TrendingUp,
  Star,
  Trophy,
  Clock,
  Home,
  Cake,
  Megaphone,
  ChefHat,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getAuthHeadersWithJson } from '../utils/authUtils';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const API_BASE = '';

// helper simples: pega o primeiro token do nome
const getPrimeiroNome = (nome?: string) => {
  if (!nome) return '';
  return nome.trim().split(/\s+/)[0] || '';
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const gamification = useGamification();

  // 👇 normaliza o nome vindo do backend (prioriza .nome; fallback .name)
  const nomeCheio = (user?.nome ?? user?.name ?? '').trim();
  const primeiroNome = getPrimeiroNome(nomeCheio) || 'Usuário';

  const [loading, setLoading] = useState(true);
  const [aniversariantesHoje, setAniversariantesHoje] = useState<any[]>([]);
  const [ultimosPosts, setUltimosPosts] = useState<any[]>([]);
  const [cardapioDia, setCardapioDia] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[DASHBOARD] 🚀 Mounting Dashboard');
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        loadAniversariantes(),
        loadUltimosPosts(),
        loadCardapio()
      ]);
    } catch (error) {
      console.error('[DASHBOARD] Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAniversariantes = async () => {
    try {
      const { data: ocultosData } = await supabase
        .from('aniversariantes_ocultos')
        .select('cpf_ou_nome');

      const ocultosSet = new Set<string>();
      ocultosData?.forEach((item: any) => {
        ocultosSet.add(item.cpf_ou_nome);
      });

      const response = await fetch('/dados/aniversariantes.json');
      const data = await response.json();

      const hoje = new Date();
      const diaHoje = hoje.getDate();
      const mesHoje = hoje.getMonth() + 1;

      const aniversariantesDeHoje = data.filter((pessoa: any) => {
        const cpf = pessoa.cpf || pessoa.nome;
        return pessoa.dia === diaHoje && pessoa.mesNumero === mesHoje && !ocultosSet.has(cpf);
      });

      setAniversariantesHoje(aniversariantesDeHoje);
    } catch (error) {
      console.error('[DASHBOARD] Erro ao carregar aniversariantes:', error);
    }
  };

  const loadUltimosPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('mural_posts')
        .select('*')
        .eq('publicado', true)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      setUltimosPosts(data || []);
    } catch (error) {
      console.error('[DASHBOARD] Erro ao carregar posts:', error);
    }
  };

  const loadCardapio = async () => {
    try {
      const hoje = new Date();
      const dataHoje = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;

      console.log('[DASHBOARD] Buscando cardápio para data:', dataHoje);

      const { data, error } = await supabase
        .from('cardapio_itens')
        .select('*')
        .eq('data', dataHoje)
        .eq('publicado', true)
        .limit(1);

      console.log('[DASHBOARD] Resultado da query:', { data, error });

      if (error) throw error;
      setCardapioDia(data && data.length > 0 ? data[0] : null);
    } catch (error) {
      console.error('[DASHBOARD] Erro ao carregar cardápio:', error);
    }
  };

  const formatarDataPost = (dataStr: string) => {
    try {
      const date = new Date(dataStr);
      return format(date, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
    } catch {
      return '';
    }
  };

  const pontos = {
    totalPontos: gamification.userStats?.totalPoints || 0,
    breakdown: gamification.userStats?.activities.reduce((acc: any[], activity) => {
      const existing = acc.find(item => item.acao === activity.type);
      if (existing) {
        existing.total += activity.points;
        existing.count += 1;
      } else {
        acc.push({
          acao: activity.type,
          total: activity.points,
          count: 1
        });
      }
      return acc;
    }, []) || []
  };

  const ranking = gamification.allUsersStats
    .sort((a, b) => b.totalPoints - a.totalPoints)
    .map((stats, index) => ({
      nome: stats.userName,
      total_pontos: stats.totalPoints,
      foto: stats.userAvatar,
      position: index + 1,
      nivel: stats.level,
      total_acoes: stats.totalActions
    }));

  const getActionLabel = (acao: string) => {
    const labels: Record<string, string> = {
      page_visit: 'Visitas às Páginas',
      mural_like: 'Curtidas no Mural',
      mural_comment: 'Comentários no Mural',
      mural_post: 'Posts Criados',
      reaction: 'Reações no Mural',
      comment: 'Comentários',
      post_creation: 'Publicações Criadas',
      room_reservation: 'Reservas de Salas',
      reception_appointment: 'Agendamentos da Portaria',
      protein_exchange: 'Trocas de Proteína',
      equipment_request: 'Solicitações de Equipamentos',
      MURAL_LIKE: 'Curtidas no Mural', // Legacy support
      MURAL_COMMENT: 'Comentários no Mural', // Legacy support
      RESERVA_CREATE: 'Reservas Criadas', // Legacy support
      PORTARIA_CREATE: 'Agendamentos da Portaria', // Legacy support
      TROCA_PROTEINA: 'Trocas de Proteína' // Legacy support
    };
    return labels[acao] || acao;
  };

  const getUserRank = () => {
    if (ranking.length === 0) return '-';
    const userRanking = ranking.find(r => r.nome === (user?.nome ?? user?.name));
    return userRanking ? userRanking.position : '-';
  };

  const getUserLevel = (totalPontos: number) => {
    if (totalPontos < 50) return 1;
    if (totalPontos < 100) return 2;
    if (totalPontos < 200) return 3;
    if (totalPontos < 350) return 4;
    if (totalPontos < 550) return 5;
    if (totalPontos < 800) return 6;
    if (totalPontos < 1100) return 7;
    if (totalPontos < 1500) return 8;
    if (totalPontos < 2000) return 9;
    return 10;
  };

  const stats = [
    {
      title: 'Pontos Totais',
      value: pontos.totalPontos.toString(),
      icon: Star,
      color: '#F59E0B'
    },
    {
      title: 'Posição no Ranking',
      value: `#${getUserRank()}`,
      icon: Trophy,
      color: '#3d8b5e'
    },
    {
      title: 'Nível Atual',
      value: (gamification.userStats?.level || 1).toString(),
      icon: TrendingUp,
      color: '#3d8b5e'
    },
    {
      title: 'Ações Realizadas',
      value: (gamification.userStats?.totalActions || 0).toString(),
      icon: MessageSquare,
      color: '#5da87a'
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div
          className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-8 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Olá, {primeiroNome}! 👋
              </h1>
              <p className="text-white text-opacity-90 mb-4">
                Bem-vindo de volta à Intranet do Grupo Cropfield
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-300 fill-current" />
                  <span className="font-semibold">{pontos.totalPontos} pontos</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Trophy className="w-5 h-5 text-yellow-300" />
                  <span className="font-semibold">Nível {gamification.userStats?.level || 1}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded-full">
                    #{getUserRank()} no ranking
                  </span>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-on-surface dark:text-gray-100 mt-2">{stat.value}</p>
                </div>
                <div
                  className="rounded-lg p-3"
                  style={{ backgroundColor: stat.color }}
                >
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Atualizações da Intranet */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-on-surface dark:text-gray-100">Atualizações da Intranet</h2>
              <Megaphone className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-6">
              {/* Aniversariantes do Dia */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Cake className="w-5 h-5 text-pink-500" />
                  <h3 className="font-semibold text-on-surface dark:text-gray-100">Aniversariantes de Hoje</h3>
                </div>
                {aniversariantesHoje.length > 0 ? (
                  <div className="space-y-2">
                    {aniversariantesHoje.map((pessoa, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border border-primary-light/40 bg-primary-light/10"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-primary-light/30"
                          >
                            <Cake className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-on-surface dark:text-gray-100">{pessoa.nome}</p>
                            <p className="text-xs text-secondary dark:text-gray-400">Parabéns!</p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/aniversariantes')}
                          className="text-sm font-medium hover:underline text-primary"
                        >
                          Parabenizar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-secondary dark:text-gray-400 pl-7">Nenhum aniversariante hoje</p>
                )}
              </div>

              {/* Última Publicação do Mural */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-on-surface dark:text-gray-100">Última Publicação no Mural</h3>
                </div>
                {ultimosPosts.length > 0 ? (
                  <div
                    onClick={() => navigate('/mural')}
                    className="p-4 rounded-lg border border-primary-light/40 bg-primary-light/10 transition-colors cursor-pointer hover:bg-primary-light/15"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center bg-primary-light/30"
                        >
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-on-surface dark:text-gray-100 text-sm">
                            {ultimosPosts[0].autor_nome}
                          </p>
                          <p className="text-xs text-secondary dark:text-gray-400">
                            {ultimosPosts[0].autor_setor}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-on-surface dark:text-gray-200 line-clamp-3 mb-2">
                      {ultimosPosts[0].conteudo}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-secondary dark:text-gray-400">
                        {formatarDataPost(ultimosPosts[0].created_at)}
                      </p>
                      <button
                        className="text-xs font-medium flex items-center space-x-1 hover:underline text-primary"
                      >
                        <span>Ver no mural</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-secondary dark:text-gray-400 pl-7">Nenhuma publicação recente</p>
                )}
              </div>

              {/* Cardápio do Dia */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <ChefHat className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-on-surface dark:text-gray-100">Cardápio do Dia</h3>
                </div>
                {cardapioDia ? (
                  <div
                    className="p-4 rounded-lg border border-primary-light/40 bg-primary-light/10"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-bold text-on-surface dark:text-gray-100">{cardapioDia.dia_semana}</p>
                        <p className="text-xs text-secondary dark:text-gray-400">{cardapioDia.data}</p>
                      </div>
                      <div
                        className="rounded-full px-3 py-1 bg-primary-light/30"
                      >
                        <span className="text-xs font-bold text-primary">HOJE</span>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-on-surface dark:text-gray-200">
                      <div>
                        <span className="font-semibold block mb-1">Proteínas:</span>
                        <ul className="ml-4 space-y-1">
                          {cardapioDia.proteinas?.map((item: string, idx: number) => (
                            <li key={idx} className="text-xs">• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-semibold block mb-1">Acompanhamentos:</span>
                        <ul className="ml-4 space-y-1">
                          {cardapioDia.acompanhamentos?.map((item: string, idx: number) => (
                            <li key={idx} className="text-xs">• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-semibold block mb-1">Saladas:</span>
                        <ul className="ml-4 space-y-1">
                          {cardapioDia.saladas?.map((item: string, idx: number) => (
                            <li key={idx} className="text-xs">• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex">
                        <span className="font-semibold min-w-[100px]">Sobremesa:</span>
                        <span>{cardapioDia.sobremesa}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/cardapio')}
                      className="mt-4 w-full text-center text-xs font-medium flex items-center justify-center space-x-1 hover:underline text-primary"
                    >
                      <span>Ver cardápio da semana</span>
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-secondary dark:text-gray-400 pl-7">Cardápio não disponível para hoje</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Users Ranking */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-on-surface dark:text-gray-100">Top 5 Usuários</h2>
              <span className="text-sm text-secondary dark:text-gray-400">{ranking.length} usuários ativos</span>
            </div>
            <div className="space-y-4">
              {ranking.slice(0, 5).map((topUser) => {
                const isCurrentUser = topUser.nome === (user?.nome ?? user?.name);
                return (
                  <div
                    key={topUser.position}
                    className={`flex items-center space-x-4 p-3 rounded-lg transition-colors ${
                      isCurrentUser ? 'border border-primary-light/40 bg-primary-light/10' : 'hover:bg-surface-container-low dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        topUser.position === 1 ? 'bg-yellow-500 text-white' :
                        topUser.position === 2 ? 'bg-gray-400 text-white' :
                        topUser.position === 3 ? 'bg-orange-500 text-white' : 'bg-surface-container text-secondary'
                      }`}>
                        {topUser.position}
                      </span>
                      <img
                        src={topUser.foto || 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?w=150'}
                        alt={topUser.nome}
                        className="w-12 h-12 rounded-full object-cover border-2 border-outline-variant"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-on-surface dark:text-gray-100">{topUser.nome}</p>
                        {isCurrentUser && (
                          <span
                            className="text-xs text-white px-2 py-1 rounded-full font-medium bg-primary"
                          >
                            Você
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 mt-1">
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          <span className="text-xs text-secondary dark:text-gray-400 font-medium">{topUser.total_pontos} pontos</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Trophy className="w-3 h-3 text-primary" />
                          <span className="text-xs text-secondary dark:text-gray-400">Nível {topUser.nivel}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MessageSquare className="w-3 h-3 text-primary" />
                          <span className="text-xs text-secondary dark:text-gray-400">{topUser.total_acoes} ações</span>
                        </div>
                      </div>
                    </div>
                    {topUser.position <= 3 && (
                      <div className="text-2xl">
                        {topUser.position === 1 && '🥇'}
                        {topUser.position === 2 && '🥈'}
                        {topUser.position === 3 && '🥉'}
                      </div>
                    )}
                  </div>
                );
              })}
              {ranking.length === 0 && (
                <div className="text-center py-8 text-secondary dark:text-gray-400">
                  <p>Nenhum usuário no ranking</p>
                  <p className="text-sm mt-2">Seja o primeiro a pontuar!</p>
                </div>
              )}
              {ranking.length > 5 && (
                <div className="pt-4 border-t border-outline-variant dark:border-gray-700">
                  <div className="text-center">
                    <p className="text-sm text-secondary dark:text-gray-400">
                      Sua posição:{' '}
                      <span className="font-bold text-primary">
                        #{getUserRank()}
                      </span>{' '}
                      de {ranking.length}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
